// AI-action input media classification, shared by the payload renderer (Answer) and the
// height estimators (answerGeometryProjection / answerHeightFingerprint).
//
// When a post carries an AI action, the media the action consumed (image/video/audio inputs
// and references) is usually also attached to the answer. Those attachments are provenance,
// not content: Answer renders them as a compact thumbnail strip instead of full payload
// tiles, and the estimators reserve the strip constant instead of tile buckets. Both sides
// MUST classify with this module or reserved heights drift from rendered heights.
import { areSamePreviewUrl } from 'components/answers/payloadRemoval'

export const aiInputMediaIdentityValues = item =>
    [
        item?.attachmentId,
        item?.attachment_id,
        item?.blobId,
        item?.blob_id,
        item?.sourceAttachmentId,
        item?.sourceBlobId,
    ]
        .filter(Boolean)
        .map(value => value.toString())

export const aiInputDescriptorUrls = input =>
    [
        input?.url,
        input?.preview_url,
        input?.previewUrl,
        input?.preview_image_url,
        input?.previewImageUrl,
        input?.urls?.original,
        input?.urls?.large,
        input?.urls?.medium,
        input?.urls?.small,
    ].filter(Boolean)

export const aiInputMediaItemUrls = item =>
    [
        item?.url,
        item?.thumbnailUrl,
        item?.previewUrl,
        item?.preview_url,
        item?.urls?.original,
        item?.urls?.large,
        item?.urls?.medium,
        item?.urls?.small,
    ].filter(Boolean)

// Renderer-shaped adapters for legacy answer media. The payload renderer normalizes these
// fields into camel-cased payload items before matching; estimators and fingerprints use the
// same shapes so identity matching cannot drift between render and reserve paths.
export const answerImageInputClassificationItem = answer => ({
    ai_input: answer?.answer_image_ai_input,
    attachmentId: answer?.image_attachment_id || null,
    blobId: answer?.image_blob_id || null,
    url: answer?.local_answer_image_url || answer?.answer_image_url || null,
    previewUrl: answer?.answer_image_url || null,
})

export const answerRecordingInputClassificationItem = answer => ({
    ai_input: answer?.recording_ai_input,
    attachmentId:
        answer?.mp4_recording_attachment_id ||
        answer?.recording_attachment_id ||
        answer?.recording_info?.attachment_id ||
        null,
    blobId:
        answer?.mp4_recording_blob_id ||
        answer?.recording_blob_id ||
        answer?.recording_info?.blob_id ||
        null,
    url: answer?.mp4_recording_url || answer?.recording_url || null,
    thumbnailUrl: answer?.recording_thumbnail_url || null,
})

export const fileInputClassificationItem = file => ({
    ...file,
    attachmentId: file?.id || file?.attachment_id || file?.attachmentId || null,
    blobId: file?.blob_id || file?.blobId || null,
})

// Inputs never consume output slots. Only six inputs are visible because the strip is a single
// horizontal row, but every input must stay excluded from the independently capped payload.
export const partitionAiInputMediaItems = (
    items,
    { maxVisibleInputs = 6, maxPayloadItems = 4 } = {},
) => {
    const sourceItems = Array.isArray(items) ? items.filter(Boolean) : []
    const isInput = item => !!(item?.aiInputRole && item?.url)
    return {
        aiInputMediaItems: sourceItems.filter(isInput).slice(0, maxVisibleInputs),
        payloadItems: sourceItems.filter(item => !isInput(item)).slice(0, maxPayloadItems),
    }
}

// Flattens one run's action_payload media inputs into descriptors carrying the input payload
// plus enough ordering metadata to prefer the most recent action when several match.
export const aiInputDescriptorsForRun = run => {
    const actionPayload = run?.action_payload || run?.actionPayload || {}
    const inputs = actionPayload?.inputs || {}
    const references = inputs?.references || {}
    const runId = run?.id || run?.ai_run_id || null
    const descriptors = []
    const addDescriptor = ({ input, kind, role, index = null }) => {
        if (!input) return
        descriptors.push({
            input,
            kind,
            role,
            index,
            runId,
            actionIndex: Number(run?.action_index ?? run?.actionIndex ?? 0),
            generationIndex: Number(run?.generation_index ?? run?.generationIndex ?? 0),
        })
    }

    addDescriptor({ input: inputs.image, kind: 'image', role: 'input' })
    addDescriptor({ input: inputs.start_image, kind: 'image', role: 'start_frame' })
    addDescriptor({ input: inputs.end_image, kind: 'image', role: 'end_frame' })
    addDescriptor({ input: inputs.video, kind: 'video', role: 'input' })
    addDescriptor({ input: inputs.audio, kind: 'audio', role: 'input' })
    const videoInputs = Array.isArray(inputs.videos) ? inputs.videos : []
    const referenceImages = Array.isArray(references.images) ? references.images : []
    const referenceVideos = Array.isArray(references.videos) ? references.videos : []
    const referenceAudios = Array.isArray(references.audios) ? references.audios : []

    videoInputs.forEach((input, index) =>
        addDescriptor({ input, kind: 'video', role: 'input', index }),
    )
    referenceImages.forEach((input, index) =>
        addDescriptor({ input, kind: 'image', role: 'reference', index }),
    )
    referenceVideos.forEach((input, index) =>
        addDescriptor({ input, kind: 'video', role: 'reference', index }),
    )
    referenceAudios.forEach((input, index) =>
        addDescriptor({ input, kind: 'audio', role: 'reference', index }),
    )

    return descriptors
}

// Most-recent action first, mirroring the renderer's labeling precedence.
export const aiInputDescriptorsForAnswer = answer =>
    (Array.isArray(answer?.ai_runs) ? answer.ai_runs : [])
        .flatMap(run => aiInputDescriptorsForRun(run))
        .sort((left, right) => {
            const actionDiff = right.actionIndex - left.actionIndex
            if (actionDiff) return actionDiff
            return right.generationIndex - left.generationIndex
        })

// Identity ids (attachment/blob) win when both sides carry them; otherwise fall back to
// normalized preview-url equality.
export const aiInputMatchesMediaItem = (item, input) => {
    const itemIdentityValues = aiInputMediaIdentityValues(item)
    const inputIdentityValues = aiInputMediaIdentityValues(input)
    if (itemIdentityValues.length && inputIdentityValues.length) {
        return itemIdentityValues.some(value => inputIdentityValues.includes(value))
    }

    const itemUrls = aiInputMediaItemUrls(item)
    const inputUrls = aiInputDescriptorUrls(input)
    return itemUrls.some(itemUrl =>
        inputUrls.some(inputUrl => areSamePreviewUrl(itemUrl, inputUrl)),
    )
}

// The matching descriptor for a rendered media item, or null. `excludeSameRun` skips inputs
// declared by the item's own run so a run's output never classifies as its own input.
export const aiInputDescriptorForMediaItem = (
    item,
    descriptors,
    { excludeSameRun = true } = {},
) => {
    if (!item) return null
    return (
        (descriptors || []).find(descriptor => {
            if (
                excludeSameRun &&
                item.runId &&
                descriptor.runId &&
                item.runId === descriptor.runId
            ) {
                return false
            }
            return aiInputMatchesMediaItem(item, descriptor.input)
        }) || null
    )
}

// Height-fingerprint entries persist the classification as `ai_input: 1` because fingerprinted
// ai_runs keep only output dims (no action_payload), so the matcher cannot re-run on a stub.
export const mediaEntryIsAiInput = (entry, descriptors) => {
    if (!entry) return false
    if (entry.ai_input === 1 || entry.ai_input === true) return true
    if (!descriptors?.length) return false
    return !!aiInputDescriptorForMediaItem(entry, descriptors, { excludeSameRun: false })
}

// Matcher-shaped view of one ai_run output: identity ids under the keys the matcher reads,
// plus the owning run id so a run's own output never classifies as its input (remix chains:
// run A's output consumed by run B's input DOES classify).
export const aiRunOutputClassificationItem = (run, output) => ({
    ai_input: output?.ai_input,
    runId: run?.id || run?.ai_run_id || null,
    attachment_id: output?.id || output?.attachment_id || null,
    blob_id: output?.blob_id || output?.blobId || null,
    url: output?.url || null,
    preview_url: output?.preview_url || output?.previewUrl || null,
    thumbnailUrl: output?.thumbnailUrl || null,
    urls: output?.urls || null,
})

// A run output consumed by a LATER action renders in the input strip, not as an output tile.
export const aiRunOutputIsConsumedInput = (run, output, descriptors) => {
    const item = aiRunOutputClassificationItem(run, output)
    if (item.ai_input === 1 || item.ai_input === true) return true
    if (!descriptors?.length) return false
    return !!aiInputDescriptorForMediaItem(item, descriptors, { excludeSameRun: true })
}
